import BatteryAlertIcon from "@mui/icons-material/BatteryAlert";
import BlockIcon from "@mui/icons-material/Block";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import HeartBrokenIcon from "@mui/icons-material/HeartBroken";
import PsychologyIcon from "@mui/icons-material/Psychology";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import page7_section1 from "../../../assets/images/AboutVedaz/page7_section1.jpg";
import page7_section3 from "../../../assets/images/AboutVedaz/page7_section3.jpg";
import page7_section5 from "../../../assets/images/AboutVedaz/page7_section5.jpg";
import classes from "../AboutVedaz.styles";
import { ColorBanner, FeaturePoint } from "./CompatComponents";

interface Props {
  chapterIndex: number;
}

const EmotionalAspect = ({ chapterIndex }: Props) => {
  const { t } = useTranslation();

  return (
    <Box>
      {/* ── Section 1: Background image with glass card ───────────── */}
      <Box sx={classes.bgSection}>
        <Box
          component="img"
          src={page7_section1}
          alt=""
          sx={{ ...classes.bgSectionMedia, objectPosition: "top" }}
        />
        <Box sx={classes.bgSectionOverlay} />
        <Box sx={classes.bgSectionContent}>
          <Typography sx={classes.sectionHeading}>
            {t("about_emotional_heading")}
          </Typography>
          <Typography sx={{ ...classes.bodyText, maxWidth: "400px", mb: 0 }}>
            {t("about_emotional_s1_body")}
          </Typography>
        </Box>
      </Box>

      {/* ── Section 2: Red — no compatibility ────────────────────── */}
      <Box sx={classes.plainSection}>
        <ColorBanner
          colorKey="red"
          Icon={BlockIcon}
          header={t("about_emotional_red_header")}
          subtitle={t("about_emotional_red_subtitle")}
        />
        <Typography sx={{ ...classes.bodyText, mb: 3 }}>
          {t("about_emotional_red_intro")}
        </Typography>
        <FeaturePoint
          colorKey="red"
          Icon={PsychologyIcon}
          title={t("about_emotional_red_p1_title")}
          body={t("about_emotional_red_p1_body")}
        />
        <FeaturePoint
          colorKey="red"
          Icon={BatteryAlertIcon}
          title={t("about_emotional_red_p2_title")}
          body={t("about_emotional_red_p2_body")}
        />
        <FeaturePoint
          colorKey="red"
          Icon={RemoveCircleOutlineIcon}
          title={t("about_emotional_red_p3_title")}
          body={t("about_emotional_red_p3_body")}
        />
      </Box>

      {/* ── Section 3: Yellow — background image ─────────────────── */}
      <Box sx={classes.bgSection}>
        <Box
          component="img"
          src={page7_section3}
          alt=""
          sx={classes.bgSectionMedia}
        />
        <Box sx={classes.bgSectionOverlay} />
        <Box sx={classes.bgSectionContent}>
          <ColorBanner
            colorKey="yellow"
            Icon={HeartBrokenIcon}
            header={t("about_emotional_yellow_header")}
            subtitle={t("about_emotional_yellow_subtitle")}
          />
          <Typography sx={{ ...classes.bodyText, maxWidth: "420px" }}>
            {t("about_emotional_yellow_body1")}
          </Typography>
          <Typography sx={{ ...classes.bodyText, maxWidth: "420px" }}>
            {t("about_emotional_yellow_body2")}
          </Typography>
          <Typography sx={{ ...classes.bodyText, maxWidth: "420px", mb: 0 }}>
            {t("about_emotional_yellow_body3")}
          </Typography>
        </Box>
      </Box>

      {/* ── Section 4: Light Green — numbered list layout ────────── */}
      <Box sx={classes.plainSection}>
        <ColorBanner
          colorKey="lightGreen"
          Icon={FavoriteBorderIcon}
          header={t("about_emotional_lightGreen_header")}
          subtitle={t("about_emotional_lightGreen_subtitle")}
        />
        <Box sx={{ mb: 2.5 }}>
          {[
            t("about_emotional_lightGreen_body1"),
            t("about_emotional_lightGreen_body2"),
            t("about_emotional_lightGreen_body3"),
            t("about_emotional_lightGreen_body4"),
            t("about_emotional_lightGreen_body5"),
          ].map((text, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
                py: 1.5,
                borderBottom: i < 4 ? "1px solid rgba(139,195,74,0.1)" : "none",
              }}
            >
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "#8BC34A",
                  opacity: 0.7,
                  minWidth: "20px",
                  mt: "2px",
                  letterSpacing: "0.5px",
                }}
              >
                {String(i + 1).padStart(2, "0")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "rgba(255,255,255,0.72)",
                  lineHeight: 1.7,
                }}
              >
                {text}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            py: 1.5,
            px: 2,
            borderLeft: "3px solid #8BC34A",
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              color: "rgba(139,195,74,0.85)",
              lineHeight: 1.7,
            }}
          >
            {t("about_emotional_lightGreen_note")}
          </Typography>
        </Box>
      </Box>

      {/* ── Section 5: Dark Green — background image ─────────────── */}
      <Box sx={classes.bgSection}>
        <Box
          component="img"
          src={page7_section5}
          alt=""
          sx={classes.bgSectionMedia}
        />
        <Box sx={classes.bgSectionOverlay} />
        <Box sx={classes.bgSectionContent}>
          <ColorBanner
            colorKey="darkGreen"
            Icon={FavoriteIcon}
            header={t("about_emotional_darkGreen_header")}
            subtitle={t("about_emotional_darkGreen_subtitle")}
          />
          <Typography sx={{ ...classes.bodyText, maxWidth: "420px" }}>
            {t("about_emotional_darkGreen_body1")}
          </Typography>
          <Typography sx={{ ...classes.bodyText, maxWidth: "420px" }}>
            {t("about_emotional_darkGreen_body2")}
          </Typography>
          <Typography sx={{ ...classes.bodyText, maxWidth: "420px", mb: 0 }}>
            {t("about_emotional_darkGreen_body3")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default EmotionalAspect;
